import { useEffect, useRef, useState } from "react";
import { Pen, Eraser, Undo2, RotateCcw, Check, X } from "lucide-react";

// Loads `imageSrc` (a data URL or blob URL) into a canvas, lets the user
// draw freehand annotations on top, then flattens to a PNG blob via
// canvas.toBlob() when they accept.
//
// Tools: pen (multiple colors), eraser, undo, clear, accept, cancel.
// Strokes are stored as separate paths so undo is O(strokes-replayed).
//
// Sized to fit the FeedbackWidget — caps to a max width, scales the image
// proportionally on a HiDPI-aware canvas. Devices above ~480px wide get
// crisp 2x rendering; smaller screens fall back to 1x to keep memory sane.
const STROKE_COLORS = ["#ef4444", "#facc15", "#22d3ee", "#22c55e", "#1f2937"];
const STROKE_WIDTH = 4;

export default function FeedbackAnnotator({ imageSrc, onAccept, onCancel }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const strokesRef = useRef([]); // each: {color, points: [[x,y],...]}
  const currentRef = useRef(null);
  const [tool, setTool] = useState("pen"); // "pen" or "eraser"
  const [color, setColor] = useState(STROKE_COLORS[0]);
  const [ready, setReady] = useState(false);
  const dprRef = useRef(1);

  // Load + size the canvas once the image arrives
  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      // Size the canvas to fit container width, max 600px
      const containerW = Math.min(containerRef.current?.clientWidth || 480, 600);
      const scale = containerW / img.naturalWidth;
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);

      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio > 1 ? 2 : 1;
      dprRef.current = dpr;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      redraw();
      setReady(true);
    };
    img.src = imageSrc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc]);

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    const dpr = dprRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    ctx.drawImage(imgRef.current, 0, 0, canvas.width / dpr, canvas.height / dpr);

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = STROKE_WIDTH;
    for (const stroke of strokesRef.current) {
      ctx.strokeStyle = stroke.color;
      ctx.beginPath();
      stroke.points.forEach(([x, y], i) => {
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
  };

  const xyFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return [t.clientX - rect.left, t.clientY - rect.top];
  };

  const onDown = (e) => {
    e.preventDefault();
    if (!ready) return;
    if (tool === "eraser") {
      // simple eraser: drop the last stroke
      strokesRef.current.pop();
      redraw();
      return;
    }
    const [x, y] = xyFromEvent(e);
    currentRef.current = { color, points: [[x, y]] };
  };

  const onMove = (e) => {
    if (!currentRef.current) return;
    e.preventDefault();
    const [x, y] = xyFromEvent(e);
    currentRef.current.points.push([x, y]);
    // draw incrementally for responsiveness
    const ctx = canvasRef.current.getContext("2d");
    const pts = currentRef.current.points;
    if (pts.length < 2) return;
    ctx.strokeStyle = currentRef.current.color;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = STROKE_WIDTH;
    ctx.beginPath();
    ctx.moveTo(...pts[pts.length - 2]);
    ctx.lineTo(...pts[pts.length - 1]);
    ctx.stroke();
  };

  const onUp = () => {
    if (currentRef.current) {
      strokesRef.current.push(currentRef.current);
      currentRef.current = null;
    }
  };

  const undo = () => { strokesRef.current.pop(); redraw(); };
  const clear = () => { strokesRef.current = []; redraw(); };

  const accept = () => {
    canvasRef.current.toBlob(
      (blob) => onAccept(blob),
      "image/png",
      0.92,
    );
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-2 p-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          onClick={() => setTool("pen")}
          className={`p-1.5 rounded ${tool === "pen" ? "bg-button-primary text-white" : "bg-bg-secondary text-text-secondary"}`}
          title="Pen"
        ><Pen className="w-3.5 h-3.5" /></button>
        <button
          onClick={() => setTool("eraser")}
          className={`p-1.5 rounded ${tool === "eraser" ? "bg-button-primary text-white" : "bg-bg-secondary text-text-secondary"}`}
          title="Eraser (removes last stroke)"
        ><Eraser className="w-3.5 h-3.5" /></button>
        <span className="border-l border-border-default mx-1 h-5" />
        {STROKE_COLORS.map((c) => (
          <button
            key={c}
            onClick={() => { setColor(c); setTool("pen"); }}
            className={`w-5 h-5 rounded-full border-2 ${color === c ? "border-text-primary" : "border-border-default"}`}
            style={{ backgroundColor: c }}
            title={`color ${c}`}
            aria-label={`color ${c}`}
          />
        ))}
        <span className="border-l border-border-default mx-1 h-5" />
        <button onClick={undo} className="p-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary" title="Undo">
          <Undo2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={clear} className="p-1.5 rounded bg-bg-secondary text-text-secondary hover:text-text-primary" title="Clear">
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="bg-bg-secondary/30 rounded p-1 max-h-[55vh] overflow-auto">
        <canvas
          ref={canvasRef}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
          onTouchStart={onDown}
          onTouchMove={onMove}
          onTouchEnd={onUp}
          className="block touch-none cursor-crosshair"
          style={{ imageRendering: "auto" }}
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 rounded text-sm text-text-secondary hover:bg-bg-secondary/40 flex items-center gap-1"
        >
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
        <button
          onClick={accept}
          disabled={!ready}
          className="px-3 py-1.5 rounded text-sm bg-button-primary text-white hover:opacity-90 flex items-center gap-1 disabled:opacity-50"
        >
          <Check className="w-3.5 h-3.5" /> Use this
        </button>
      </div>
    </div>
  );
}
